import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useActivationConsoleState } from "../../../hooks/useActivationConsoleState";
import { KPI_DETAIL_COPY, PRESET_CITY_CHIPS, TAB_IDS } from "../../../constants/activationConsoleData";
import ActivationSprintTab from "./ActivationSprintTab";
import ActivationPlaybookTab from "./ActivationPlaybookTab";
import ActivationClubsTab from "./ActivationClubsTab";
import ActivationScriptsTab from "./ActivationScriptsTab";
import ActivationCopyPasteTab from "./ActivationCopyPasteTab";
import { copyActivationText } from "./activationClipboard";

const TAB_LABELS = {
  sprint: "Sprint",
  playbook: "Playbook",
  clubs: "Clubs",
  scripts: "DM Scripts",
  copypaste: "Copy-Paste Kit",
};

export default function ActivationView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const tab = TAB_IDS.includes(tabParam) ? tabParam : "sprint";

  const setTab = useCallback(
    (next) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set("tab", next);
        return p;
      });
    },
    [setSearchParams]
  );

  const act = useActivationConsoleState();
  const {
    state,
    derived,
    setCity,
    duplicateCity,
    exportJson,
    getTaskScript,
    toggleTask,
    dtnMarkDone,
    dtnSkip,
    toggleFieldCheck,
    toggleGateCheck,
    qrTestPass,
    addSignal,
    addFriction,
    updateClubStatus,
    toggleDensity,
    addClub,
    updateKpi,
    extraCityKeys,
  } = act;

  const [fieldMode, setFieldMode] = useState(false);
  const [signalModalOpen, setSignalModalOpen] = useState(false);
  const [modalSignalText, setModalSignalText] = useState("");
  const [modalSignalType, setModalSignalType] = useState("strong");
  const [modalSignalSource, setModalSignalSource] = useState("organizer");
  const [playModal, setPlayModal] = useState(null);
  const [kpiModalKey, setKpiModalKey] = useState(null);
  const [kpiInput, setKpiInput] = useState("0");
  const [addClubOpen, setAddClubOpen] = useState(false);
  const [newClub, setNewClub] = useState({
    name: "",
    loc: "",
    runs: "",
    priority: "high",
    status: "pending",
    notes: "",
  });
  const [dupOpen, setDupOpen] = useState(false);
  const [dupName, setDupName] = useState("");
  const [dupKey, setDupKey] = useState("");
  const [dupError, setDupError] = useState("");
  const [playCopyId, setPlayCopyId] = useState(null);

  useEffect(() => {
    if (fieldMode) {
      setTab("sprint");
      window.scrollTo(0, 0);
    }
  }, [fieldMode, setTab]);

  useEffect(() => {
    if (kpiModalKey) {
      setKpiInput(String(state.kpis[kpiModalKey] ?? 0));
    }
  }, [kpiModalKey, state.kpis]);

  const handleSaveModalSignal = () => {
    const t = modalSignalText.trim();
    if (!t) return;
    addSignal(t, modalSignalType, modalSignalSource);
    setModalSignalText("");
    setSignalModalOpen(false);
  };

  const handleDuplicate = () => {
    const r = duplicateCity(dupName, dupKey);
    if (!r.ok) {
      setDupError(r.error || "Invalid");
      return;
    }
    setDupError("");
    setDupOpen(false);
    setDupName("");
    setDupKey("");
    setCity(r.key);
  };

  const handleAddClub = () => {
    const name = newClub.name.trim();
    if (!name) return;
    addClub({
      name,
      loc: newClub.loc.trim() || "NYC",
      runs: newClub.runs.trim() || "TBD",
      priority: newClub.priority,
      status: newClub.status,
      notes: newClub.notes.trim(),
    });
    setAddClubOpen(false);
    setNewClub({
      name: "",
      loc: "",
      runs: "",
      priority: "high",
      status: "pending",
      notes: "",
    });
  };

  const handleKpiSave = () => {
    if (!kpiModalKey) return;
    const v = parseInt(kpiInput, 10);
    updateKpi(kpiModalKey, Number.isFinite(v) ? v : 0);
    setKpiModalKey(null);
  };

  const openPlay = (p) => {
    setPlayModal(p);
    setPlayCopyId(null);
  };

  const cityLabel = (key) => {
    const preset = PRESET_CITY_CHIPS.find((c) => c.key === key);
    if (preset) return preset.label;
    const extra = state.cities[key];
    return extra?.name || key;
  };

  const isNyc = state.city === "nyc";
  const cityActive = !!state.cities[state.city];
  const phaseLabel = isNyc ? "Phase 1 — Seeding" : cityActive ? "Active" : "Not Started";
  const phaseClass = isNyc || cityActive ? "on" : "warn";

  return (
    <div className={`activation-console ${fieldMode ? "activation-console--field" : ""}`}>
      <div className="ac-page">
        <div className="ac-field-topbar">
          <div>
            <div className="ac-eyebrow" style={{ fontSize: 9, marginBottom: 3 }}>
              RunCards
            </div>
            <div className="ac-title ac-serif" style={{ fontSize: 20 }}>
              Field Mode
            </div>
          </div>
          <button
            type="button"
            className="ac-btn field-btn active"
            onClick={() => setFieldMode(false)}
          >
            ✕ Exit Field Mode
          </button>
        </div>

        <div className="ac-topbar ac-topbar-full">
          <div className="ac-tb-left">
            <div className="ac-eyebrow">RunCards · NYC Activation</div>
            <div className="ac-title ac-serif">
              Activation Console{" "}
              <span style={{ fontSize: 13, fontWeight: 400, color: "var(--muted)", fontFamily: "inherit", letterSpacing: 0 }}>
                v4
              </span>
            </div>
            <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 6, fontStyle: "italic" }}>
              Prime directive: create 1 &quot;what&apos;s that?&quot; moment per session.
            </div>
          </div>
          <div className="ac-tb-right">
            <button
              type="button"
              className="ac-btn field-btn"
              style={{ borderRadius: 999, padding: "8px 16px", fontSize: 12 }}
              onClick={() => setFieldMode(true)}
            >
              📍 Field Mode
            </button>
            <button type="button" className="ac-btn sm" onClick={exportJson}>
              ↓ Export
            </button>
            <button type="button" className="ac-btn accent sm" onClick={() => setSignalModalOpen(true)}>
              + Log Signal
            </button>
          </div>
        </div>

        <div className="ac-city-sel-bar ac-topbar-full">
          <span className="ac-csb-lbl">City</span>
          {PRESET_CITY_CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`ac-city-chip ${state.city === c.key ? "active" : ""} ${c.upcoming ? "upcoming" : ""}`}
              onClick={() => setCity(c.key)}
            >
              {c.label}
            </button>
          ))}
          {extraCityKeys.map((k) => (
            <button
              key={k}
              type="button"
              className={`ac-city-chip ${state.city === k ? "active" : ""}`}
              onClick={() => setCity(k)}
            >
              {state.cities[k]?.name || k}
            </button>
          ))}
          <button
            type="button"
            className="ac-btn sm ghost"
            style={{ marginLeft: "auto" }}
            onClick={() => {
              setDupOpen(true);
              setDupError("");
            }}
          >
            + Duplicate City
          </button>
        </div>

        <div className="ac-status-bar ac-topbar-full">
          <span className="ac-sb-label">{state.city.toUpperCase()}</span>
          <span className={`ac-sb-pill ${phaseClass}`}>{phaseLabel}</span>
          <div className="ac-sb-divider" />
          <div className="ac-sb-kpi">
            <div className="ac-sb-kpi-val ac-serif">{state.kpis.mentions}</div>
            <div className="ac-sb-kpi-lbl">Mentions</div>
          </div>
          <div className="ac-sb-kpi">
            <div className="ac-sb-kpi-val ac-serif">{derived.activeClubCount}</div>
            <div className="ac-sb-kpi-lbl">Active Clubs</div>
          </div>
          <div className="ac-sb-kpi">
            <div className="ac-sb-kpi-val ac-serif">{state.signals.length}</div>
            <div className="ac-sb-kpi-lbl">Signals</div>
          </div>
          <div className="ac-sb-divider" />
          <span className="ac-sb-pill warn">Was RunCards checked before a run?</span>
        </div>

        <div className="ac-tabs ac-topbar-full">
          {TAB_IDS.map((id) => (
            <button
              key={id}
              type="button"
              className={`ac-tab-btn ${tab === id ? "active" : ""}`}
              onClick={() => setTab(id)}
            >
              {TAB_LABELS[id]}
            </button>
          ))}
        </div>

        <div className={`ac-view ac-view-sprint ${tab === "sprint" ? "active" : ""}`}>
          <ActivationSprintTab
            state={state}
            derived={derived}
            getTaskScript={getTaskScript}
            toggleTask={toggleTask}
            dtnMarkDone={dtnMarkDone}
            dtnSkip={dtnSkip}
            toggleFieldCheck={toggleFieldCheck}
            toggleGateCheck={toggleGateCheck}
            qrTestPass={qrTestPass}
            addSignal={addSignal}
            addFriction={addFriction}
            onOpenKpi={setKpiModalKey}
          />
        </div>

        <div className={`ac-view ${tab === "playbook" ? "active" : ""}`}>
          <ActivationPlaybookTab onOpenPlay={openPlay} />
        </div>

        <div className={`ac-view ${tab === "clubs" ? "active" : ""}`}>
          <ActivationClubsTab
            state={state}
            openAddClub={() => setAddClubOpen(true)}
            updateClubStatus={updateClubStatus}
            toggleDensity={toggleDensity}
          />
        </div>

        <div className={`ac-view ${tab === "scripts" ? "active" : ""}`}>
          <ActivationScriptsTab />
        </div>

        <div className={`ac-view ${tab === "copypaste" ? "active" : ""}`}>
          <ActivationCopyPasteTab />
        </div>
      </div>

      {/* Modals */}
      <div
        className={`ac-modal-overlay ${signalModalOpen ? "open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setSignalModalOpen(false)}
        role="presentation"
      >
        <div className="ac-modal">
          <div className="ac-modal-head">
            <div className="ac-modal-title ac-serif">Log Signal</div>
            <button type="button" className="ac-modal-close" onClick={() => setSignalModalOpen(false)}>
              ✕
            </button>
          </div>
          <div className="ac-modal-section">
            <div className="ac-modal-section-lbl">What happened?</div>
            <textarea
              className="ac-sl-input"
              style={{ width: "100%", height: 80, resize: "vertical" }}
              value={modalSignalText}
              onChange={(e) => setModalSignalText(e.target.value)}
              placeholder="e.g. Two people had RunCards open pre-run at NYRR Tuesday."
            />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div>
              <div className="ac-modal-section-lbl">Signal strength</div>
              <select
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={modalSignalType}
                onChange={(e) => setModalSignalType(e.target.value)}
              >
                <option value="strong">Strong — checked before run</option>
                <option value="mid">Mid — mentioned or shown</option>
                <option value="weak">Weak — indirect</option>
              </select>
            </div>
            <div>
              <div className="ac-modal-section-lbl">Source loop</div>
              <select
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={modalSignalSource}
                onChange={(e) => setModalSignalSource(e.target.value)}
              >
                <option value="organizer">Organizer mention</option>
                <option value="qr">QR card scan</option>
                <option value="chat">Chat / screenshot</option>
                <option value="traveler">Traveler / Google</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div style={{ background: "var(--s3)", borderRadius: 8, padding: "10px 12px", fontSize: 11, color: "var(--muted)", lineHeight: 1.5, marginTop: 12 }}>
            <strong style={{ color: "var(--ink2)" }}>Why source matters:</strong> tagging tells you which loop
            is compounding. If all signals come from one source, double down on it.
          </div>
          <button type="button" className="ac-btn green" style={{ width: "100%", justifyContent: "center", marginTop: 16 }} onClick={handleSaveModalSignal}>
            Log Signal
          </button>
        </div>
      </div>

      <div
        className={`ac-modal-overlay ${playModal ? "open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setPlayModal(null)}
        role="presentation"
      >
        {playModal && (
          <div className="ac-modal">
            <div className="ac-modal-head">
              <div className="ac-modal-title ac-serif">{playModal.title}</div>
              <button type="button" className="ac-modal-close" onClick={() => setPlayModal(null)}>
                ✕
              </button>
            </div>
            <div className="ac-modal-where">📍 {playModal.where}</div>
            <div className="ac-modal-section">
              <div className="ac-modal-section-lbl">Why it works</div>
              <div className="ac-modal-text">{playModal.why}</div>
            </div>
            <div className="ac-modal-section">
              <div className="ac-modal-section-lbl">First version</div>
              <div className="ac-modal-text">{playModal.firstVersion}</div>
            </div>
            {playModal.script ? (
              <div className="ac-modal-section">
                <div className="ac-modal-section-lbl">Message to send</div>
                <div className="ac-modal-quote">{playModal.script}</div>
                <button
                  type="button"
                  className={`ac-copy-btn ${playCopyId === playModal.id ? "copied" : ""}`}
                  onClick={() =>
                    copyActivationText(playModal.script, () => {
                      setPlayCopyId(playModal.id);
                      setTimeout(() => setPlayCopyId(null), 2000);
                    })
                  }
                >
                  {playCopyId === playModal.id ? "Copied ✓" : "Copy Message"}
                </button>
              </div>
            ) : null}
            <div className="ac-modal-section">
              <div className="ac-modal-section-lbl">Success signal</div>
              <div className="ac-modal-signal">{playModal.signal}</div>
            </div>
          </div>
        )}
      </div>

      <div
        className={`ac-modal-overlay ${kpiModalKey ? "open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setKpiModalKey(null)}
        role="presentation"
      >
        {kpiModalKey && KPI_DETAIL_COPY[kpiModalKey] && (
          <div className="ac-modal">
            <div className="ac-modal-head">
              <div className="ac-modal-title ac-serif">{KPI_DETAIL_COPY[kpiModalKey].label}</div>
              <button type="button" className="ac-modal-close" onClick={() => setKpiModalKey(null)}>
                ✕
              </button>
            </div>
            <div className="ac-modal-text">{KPI_DETAIL_COPY[kpiModalKey].desc}</div>
            <div style={{ marginTop: 20 }}>
              <div className="ac-modal-section-lbl">Update count</div>
              <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
                <input
                  type="number"
                  min={0}
                  className="ac-sl-input"
                  style={{ width: 100 }}
                  value={kpiInput}
                  onChange={(e) => setKpiInput(e.target.value)}
                />
                <button type="button" className="ac-btn accent" onClick={handleKpiSave}>
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className={`ac-modal-overlay ${addClubOpen ? "open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setAddClubOpen(false)}
        role="presentation"
      >
        <div className="ac-modal">
          <div className="ac-modal-head">
            <div className="ac-modal-title ac-serif">Add Club</div>
            <button type="button" className="ac-modal-close" onClick={() => setAddClubOpen(false)}>
              ✕
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <div className="ac-modal-section-lbl">Club name</div>
              <input
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={newClub.name}
                onChange={(e) => setNewClub((c) => ({ ...c, name: e.target.value }))}
                placeholder="e.g. North Brooklyn Runners"
              />
            </div>
            <div>
              <div className="ac-modal-section-lbl">Borough / Location</div>
              <input
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={newClub.loc}
                onChange={(e) => setNewClub((c) => ({ ...c, loc: e.target.value }))}
                placeholder="e.g. Brooklyn / McCarren Park"
              />
            </div>
            <div>
              <div className="ac-modal-section-lbl">Weekly runs</div>
              <input
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={newClub.runs}
                onChange={(e) => setNewClub((c) => ({ ...c, runs: e.target.value }))}
                placeholder="e.g. Tue AM, Thu PM, Sat AM"
              />
            </div>
            <div>
              <div className="ac-modal-section-lbl">Priority</div>
              <select
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={newClub.priority}
                onChange={(e) => setNewClub((c) => ({ ...c, priority: e.target.value }))}
              >
                <option value="high">High — anchor node candidate</option>
                <option value="mid">Mid — secondary outreach</option>
                <option value="low">Low — Phase 2</option>
              </select>
            </div>
            <div>
              <div className="ac-modal-section-lbl">Status</div>
              <select
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={newClub.status}
                onChange={(e) => setNewClub((c) => ({ ...c, status: e.target.value }))}
              >
                <option value="pending">Pending</option>
                <option value="contacted">Contacted</option>
                <option value="warm">Warm</option>
                <option value="active">Active</option>
                <option value="anchor">Anchor ★</option>
              </select>
            </div>
            <div>
              <div className="ac-modal-section-lbl">Notes</div>
              <input
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={newClub.notes}
                onChange={(e) => setNewClub((c) => ({ ...c, notes: e.target.value }))}
                placeholder="Captain contact, WhatsApp group, etc."
              />
            </div>
            <button type="button" className="ac-btn green" style={{ width: "100%", justifyContent: "center", marginTop: 4 }} onClick={handleAddClub}>
              Save Club
            </button>
          </div>
        </div>
      </div>

      <div
        className={`ac-modal-overlay ${dupOpen ? "open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setDupOpen(false)}
        role="presentation"
      >
        <div className="ac-modal">
          <div className="ac-modal-head">
            <div className="ac-modal-title ac-serif">Duplicate City</div>
            <button type="button" className="ac-modal-close" onClick={() => setDupOpen(false)}>
              ✕
            </button>
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginBottom: 20, lineHeight: 1.6 }}>
            Creates a fresh activation sprint for a new city. Carries over playbook and scripts. Resets KPIs,
            signals, clubs.
          </div>
          <div style={{ background: "var(--ink)", borderRadius: 10, padding: "16px 18px", marginBottom: 20 }}>
            <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>
              Step 0 — Before anything else
            </div>
            <div className="ac-serif" style={{ fontSize: 16, color: "white", lineHeight: 1.4 }}>
              Identify 3 anchor clubs in this city.
              <br />
              <span style={{ color: "rgba(255,255,255,0.5)", fontStyle: "italic", fontSize: 14 }}>
                Same criteria: dense, consistent, social.
              </span>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <div className="ac-modal-section-lbl">City name</div>
              <input
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={dupName}
                onChange={(e) => {
                  setDupName(e.target.value);
                  setDupError("");
                }}
                placeholder="e.g. Chicago"
              />
            </div>
            <div>
              <div className="ac-modal-section-lbl">City key (short, no spaces)</div>
              <input
                className="ac-sl-input"
                style={{ width: "100%" }}
                value={dupKey}
                onChange={(e) => {
                  setDupKey(e.target.value);
                  setDupError("");
                }}
                placeholder="e.g. chi"
              />
            </div>
            {dupError ? (
              <div style={{ fontSize: 12, color: "var(--red)", fontWeight: 600 }}>{dupError}</div>
            ) : null}
            <button type="button" className="ac-btn green" style={{ width: "100%", justifyContent: "center", marginTop: 4 }} onClick={handleDuplicate}>
              Create City Sprint
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
